/**
 * Display config for the tutorials page.
 */

import { getTutorialVideoUrlCandidates } from '@/config/storage';
import type {
  HelpSupportMode,
  HelpSupportPlatform,
  TutorialCatalogMode,
  TutorialVideo,
  TutorialVideoMode,
} from '@/types/tutorial.types';

/** Query keys for the tutorial catalog and the help-support row. */
export const tutorialKeys = {
  all: ['tutorials'] as const,
  admin: (mode: TutorialCatalogMode) => ['tutorials', 'admin', mode] as const,
  catalog: (mode: TutorialCatalogMode) => ['tutorials', 'catalog', mode] as const,
  detail: (id: string) => ['tutorials', 'detail', id] as const,
  helpRoot: ['help-support'] as const,
  help: (platform: string, mode: string) => ['help-support', platform, mode] as const,
  helpAdmin: ['help-support', 'admin'] as const,
  roles: ['tutorials', 'role-options'] as const,
};

/** Role-group codes sent to `GET /roles` for the tutorial audience picker. */
export const TUTORIAL_AUDIENCE_ROLE_GROUPS = ['admins', 'volunteers'] as const;

/** Mode choices in the tutorial form. */
export const TUTORIAL_MODE_OPTIONS: ReadonlyArray<{ value: TutorialVideoMode; label: string }> = [
  { value: 'in_person', label: 'Présentiel' },
  { value: 'remote', label: 'Distanciel' },
  { value: 'hybrid', label: 'Hybride' },
];

/** Accept attribute for the tutorial file input. */
export const TUTORIAL_VIDEO_ACCEPTED_FORMATS =
  'video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov';

/** Max upload size for a tutorial (50 MB). */
export const TUTORIAL_VIDEO_MAX_SIZE_BYTES = 50 * 1024 * 1024;

/** Phone used when `GET /help-support` is not available yet. */
export const HELP_PHONE_FALLBACK = '01 83 90 18 79';

/** Form URL used when `GET /help-support` is not available yet. */
export const HELP_URL_FALLBACK: Record<HelpSupportPlatform, Record<HelpSupportMode, string>> = {
  web: {
    remote: 'https://g3um1.share-eu1.hsforms.com/2va5cMNb1RhesPB60aq6uHw',
    in_person: 'https://g3um1.share-eu1.hsforms.com/2xhx-llTfSc-f5SVeLfvJXw',
  },
  mobile: {
    remote: 'https://share-eu1.hsforms.com/2j8_WfXPJQ6WAu9rP-XPCTgg3um1',
    in_person: 'https://g3um1.share-eu1.hsforms.com/2TYITVOVkRnyhyr6g_o9peg',
  },
};

/** The four help contexts, in the order shown in the edit panel. */
export const HELP_SUPPORT_FORM_SECTIONS: ReadonlyArray<{
  key: string;
  platform: HelpSupportPlatform;
  mode: HelpSupportMode;
  title: string;
}> = [
  {
    key: 'web:in_person',
    platform: 'web',
    mode: 'in_person',
    title: 'Administration - Présentiel',
  },
  { key: 'web:remote', platform: 'web', mode: 'remote', title: 'Administration - Distanciel' },
  { key: 'mobile:in_person', platform: 'mobile', mode: 'in_person', title: 'Usager - Présentiel' },
  { key: 'mobile:remote', platform: 'mobile', mode: 'remote', title: 'Usager - Distanciel' },
];

/** Title of the help banner under the catalog. */
export const HELP_SUPPORT_BANNER_TITLE =
  'Ce formulaire est réservé aux problèmes techniques liés à la plateforme.';

/** CTA on the help banner. */
export const HELP_SUPPORT_BANNER_CTA = 'Accéder au formulaire';

/** Lead of the referent sentence, before the phone link. */
export const HELP_SUPPORT_BANNER_PHONE_LEAD =
  'Si votre demande concerne votre accompagnement ou le suivi de vos séances, contactez votre référent par mail ou au ';

/** Legacy filename without extension, under Firebase Storage `Tutorials/`. */
interface LegacyTutorial {
  id: string;
  name: string;
  filename: string;
}

/** Official tutorial videos. Filenames = Drive file names without extension. */
const LEGACY_TUTORIALS: readonly LegacyTutorial[] = [
  {
    id: '1',
    name: 'Compte tuteur I saisie de compte rendue',
    filename: 'Compte tuteur I saisie de compte rendue',
  },
  {
    id: '2',
    name: 'Saisie des rapports journaliers des VSC',
    filename: 'Saisie des rapports journaliers des VSC',
  },
  { id: '3', name: 'Coordinateur', filename: 'Coordinateur' },
  { id: '4', name: 'Visualisation des séances', filename: 'Visualisation des séances' },
  { id: '5', name: 'Ajout de classe', filename: 'Ajout de classe' },
  {
    id: '6',
    name: 'Espace Admin I Saisie des comptes rendus',
    filename: 'Espace Admin I Saisie des comptes rendus',
  },
  { id: '7', name: 'Créer un recruteur', filename: 'Créer un recruteur' },
  { id: '8', name: 'Création de séances', filename: 'Création de séances' },
  { id: '9', name: 'Créer un volontaire', filename: 'Créer un volontaire' },
  { id: '10', name: 'Présentation plateforme tuteur', filename: 'Présentation plateforme tuteur' },
  { id: '11', name: 'Gestion des activités', filename: 'Gestion des activités' },
  { id: '12', name: 'Ajout des rôles fiche élève', filename: 'Ajout des rôles fiche élève' },
  { id: '13', name: 'Tuteurs fiche', filename: 'Tuteurs fiche' },
  { id: '14', name: 'Élèves création 2', filename: 'Élèves création 2' },
  { id: '15', name: 'Créer une spécialité', filename: 'Créer une spécialité' },
];

/** Same 15 videos, playable from Storage until the API catalog responds. */
export const TUTORIAL_VIDEO_FALLBACK: TutorialVideo[] = LEGACY_TUTORIALS.map((video) => ({
  id: video.id,
  name: video.name,
  mode: 'hybrid',
  videoUrl: getTutorialVideoUrlCandidates(video.filename)[0],
  roleIds: [],
  createdAt: '',
  updatedAt: '',
}));
